import { useMemo } from 'react';
import MixedBarLines from '../../../Components/Charts/Mixed/MixedBarLines';
import ChartHtmlLegend, {
    useChartHtmlLegend,
    useViewportWidth
} from '../../../Components/Charts/HtmlLegend/ChartHtmlLegend';

export default function PerformanceChart({ chartDataSource }) {
    const width = useViewportWidth();
    const legend = useChartHtmlLegend();
    const dataPerf = useMemo(() => {
        const labels = chartDataSource.map(p =>
            new Date(p.workoutStart).toLocaleDateString('el-GR')
        );
        const currentDateTimestamp = Date.now();
        const pastWorkouts = chartDataSource.filter(
            workout => new Date(workout.workoutStart).getTime() <= currentDateTimestamp
        );
        const futureWorkouts = chartDataSource.slice(pastWorkouts.length - 1);
        const atlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));

        const ctlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));

        const tsbData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));

        const acwrData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.acwr
        }));

        const tssData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tss
        }));

        const futureAtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));
        const futureCtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));
        const futureTsbData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));
        const futureAcwrData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.acwr
        }));

        return {
            labels,
            datasets: [
                {
                    type: 'line',
                    label: 'Fatigue (ATL)',
                    borderColor: `rgb(234, 43, 35)`,
                    borderWidth: 2,
                    fill: false,
                    data: atlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fatigue (ATL)',
                    borderColor: `rgba(234, 43, 35, 0.5)`,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    data: futureAtlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Fitness (CTL)',
                    borderColor: `rgb(34, 168, 53)`,
                    borderWidth: 2,
                    fill: false,
                    data: ctlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fitness (CTL)',
                    borderColor: `rgba(34, 168, 53, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureCtlData,
                    borderDash: [5, 5],
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Form (TSB)',
                    borderColor: `rgb(88, 05, 251)`,
                    borderWidth: 2,
                    fill: false,
                    data: tsbData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Form (TSB)',
                    borderColor: `rgba(88, 05, 251, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureTsbData,
                    spanGaps: true,
                    borderDash: [5, 5],
                    yAxisID: 'y1'
                },
                {
                    type: 'bar',
                    label: 'Stress (TSS)',
                    backgroundColor: `rgb(42, 85, 244)`,
                    borderColor: `rgb(42, 85, 244)`,
                    borderWidth: 2,
                    data: tssData,
                    maxBarThickness: 50,
                    yAxisID: 'y'
                },
            ],
            pastWorkouts,
            futureWorkouts,
            acwrData,
            futureAcwrData
        };
    }, [chartDataSource]);

    const chartOptions = useMemo(() => {
        const stressValues = [...dataPerf.datasets[6].data]
            .map(d => d.y)
            .filter(y => y !== undefined);
        const maxStress = Math.ceil(Math.max(...stressValues) * 1.1);
        const lineValues = [
            ...dataPerf.datasets[0].data,
            ...dataPerf.datasets[1].data,
            ...dataPerf.datasets[2].data,
            ...dataPerf.datasets[3].data,
            ...dataPerf.datasets[4].data,
            ...dataPerf.datasets[5].data
        ]
            .map(d => d.y)
            .filter(y => y !== undefined);

        const maxLine = Math.ceil(Math.max(...lineValues) * 1.1);
        const minLine = Math.floor(Math.min(...lineValues) * 1.1);

        // Scale the stress axis minimum by the ratio of maximums
        const stressMin = (maxStress / maxLine) * minLine;

        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Performance',
                    font: {
                        size: width < 768 ? 14 : 16
                    }
                },
                legend: {
                    // Replaced by the accessible HTML legend (ChartHtmlLegend), same labels.
                    display: false,
                    labels: {
                        usePointStyle: true,
                        padding: width < 768 ? 8 : 10,
                        font: {
                            size: width < 768 ? 10 : 12
                        },
                        generateLabels: chart => {
                            return chart.data.datasets
                                .map((dataset, i) => ({
                                    text: dataset.label,
                                    strokeStyle: dataset.borderColor || dataset.backgroundColor,
                                    fillStyle: dataset.borderColor || dataset.backgroundColor,
                                    pointStyle: 'line',
                                    hidden: !chart.isDatasetVisible(i),
                                    datasetIndex: i
                                }))
                                .filter(label => !label.text.includes('Future'));
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        maxRotation: width < 768 ? 45 : 0,
                        minRotation: width < 768 ? 45 : 0,
                        font: {
                            size: width < 768 ? 9 : 11
                        }
                    }
                },
                y: {
                    type: 'linear',
                    position: 'left',
                    title: {
                        display: width >= 480,
                        text: 'Stress',
                        color: 'rgb(148, 148, 148)',
                        font: {
                            size: width < 768 ? 10 : 12
                        }
                    },
                    ticks: {
                        font: {
                            size: width < 768 ? 9 : 11
                        }
                    },
                    min: stressMin,
                    max: maxStress,
                    grid: {
                        drawOnChartArea: false
                    }
                },
                y1: {
                    type: 'linear',
                    position: 'right',
                    title: {
                        display: width >= 480,
                        text: 'Fatigue / Fitness / Form',
                        color: 'rgb(148, 148, 148)',
                        font: {
                            size: width < 768 ? 10 : 12
                        }
                    },
                    ticks: {
                        font: {
                            size: width < 768 ? 9 : 11
                        }
                    },
                    min: minLine,
                    max: maxLine,
                    grid: {
                        drawOnChartArea: true
                    }
                }
            },
            animations: {
                tension: {
                    duration: 1500,
                    easing: 'easeInOutBounce',
                    from: 1,
                    to: 0
                }
            }
        };
    }, [dataPerf, width]);

    // Chart.js legend placement: top on desktop, bottom on small screens, definitions-only below 480px.
    const legendPosition = width < 768 ? 'bottom' : 'top';
    const legendNode = position =>
        position === legendPosition && (
            <ChartHtmlLegend
                items={legend.items}
                onToggle={legend.toggle}
                definitionsOnly={width < 480}
                fontSize={width < 768 ? 10 : 12}
            />
        );

    return (
        <div
            style={{
                width: '100%',
                height: width < 768 ? '400px' : '500px',
                maxHeight: width < 768 ? 'none' : '50vh',
                padding: width < 768 ? '10px' : '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: width < 768 ? '10px' : '20px',
                position: 'relative'
            }}
        >
            {width < 768 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(51, 78, 172, 0.9)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}
                >
                    Pinch to zoom
                </div>
            )}
            {legendNode('top')}
            <MixedBarLines data={dataPerf} options={chartOptions} plugins={legend.plugins} />
            {legendNode('bottom')}
        </div>
    );
}
